import { Profile } from 'oidc-client';
import { LINEUP_LIMIT } from '../config/settings';
import { DropdownOption } from '../core/models/DropdownOption';
import {
    ClubsClient,
    CompetitionTeamsClient,
    CreateClubCommandOfClub,
    CreateClubPlayerCommandOfClubPlayer,
    CreateCompetitionTeamCommand,
    CreatePersonCommandOfPerson,
    CreatePlayerCommand,
    CreateTeamCommandOfTeam,
    CreateTeamPlayerCommandOfTeamPlayer,
    PersonsClient,
    PlayerPositionEnum,
    TeamDetailVm,
    TeamsClient,
} from '../utils/api';

/*
 * Statistics mode helpers for opponents a club does not want to maintain.
 *
 * Everything here is composed from the regular endpoints, so no API changes are
 * needed. ponytail: the club, team and players are separate requests instead of
 * one transaction, so a failure halfway leaves the opponent partially created
 * and the user finishes it by hand. Upgrade path is a single backend command.
 */

// Squads are filled up to the lineup limit so the same placeholders work for
// every rule set the club plays under.
export const DUMMY_PLAYER_COUNT = LINEUP_LIMIT;

const DUMMY_PLAYER_NAME = 'Opp';
const DUMMY_BIRTH_DATE = new Date(Date.UTC(2000, 0, 1));
const DEFAULT_CATEGORY_NAME = 'first team';

export interface CreateOpponentTeamRequest {
    name: string;
    categoryId: number;
    federationId?: number;
    competitionId?: number;
}

/*
 * Clubs and teams can only be created for the federation the user owns, so the
 * opponent club has to carry that federation. Admins have no such claim and are
 * allowed to create without one.
 */
export const getOwnedFederationId = (userProfile: Profile): number | undefined => {
    const ownedFederationId = parseInt(userProfile && userProfile['federationOwner'], 10);

    return isNaN(ownedFederationId) ? undefined : ownedFederationId;
};

export const findDefaultCategoryOption = (
    categories: DropdownOption[]
): DropdownOption | undefined =>
    categories.find((c: DropdownOption): boolean =>
        c.name.toLowerCase().startsWith(DEFAULT_CATEGORY_NAME)
    );

/*
 * A team always needs a club, but an opponent is entered as a single name. The
 * club is created behind the scenes with that same name so the user never has to
 * think about it.
 */
export const createOpponentTeam = async (
    request: CreateOpponentTeamRequest
): Promise<TeamDetailVm> => {
    const name = request.name.trim();

    const club = await new ClubsClient().create(
        new CreateClubCommandOfClub({
            name,
            nameUniversal: name,
            shortName: undefined,
            description: undefined,
            activeFrom: undefined,
            activeTo: undefined,
            venue: undefined,
            president: undefined,
            address: undefined,
            web: undefined,
            email: undefined,
            phone: undefined,
            facebook: undefined,
            youtube: undefined,
            twitter: undefined,
            instagram: undefined,
            logo: undefined,
            isPro: false,
            isNational: false,
            federationId: request.federationId,
            countryId: undefined,
            cityId: undefined,
            teamCategories: undefined,
        })
    );

    const team = await new TeamsClient().create(
        new CreateTeamCommandOfTeam({
            name,
            nameUniversal: name,
            shortName: undefined,
            description: undefined,
            activeFrom: undefined,
            activeTo: undefined,
            venue: undefined,
            address: undefined,
            web: undefined,
            email: undefined,
            phone: undefined,
            facebook: undefined,
            youtube: undefined,
            twitter: undefined,
            instagram: undefined,
            logo: undefined,
            isPro: false,
            clubId: club.id,
            categoryId: request.categoryId,
            countryId: undefined,
            cityId: undefined,
        })
    );

    if (request.competitionId) {
        await new CompetitionTeamsClient().create(
            new CreateCompetitionTeamCommand({
                description: undefined,
                isDisqualified: false,
                isSeed: false,
                competitionId: request.competitionId,
                teamId: team.id,
            })
        );
    }

    return team;
};

/*
 * Placeholder squad for opponents nobody wants to type in: caps 1 to the lineup
 * limit, cap 1 as goalkeeper so a match report can be started.
 *
 * Players are linked to the team and to the team's club, so the opponent shows a
 * squad on both pages the way a hand entered team does.
 */
export const fillTeamWithDummyPlayers = async (
    teamId: number,
    onProgress?: (created: number, total: number) => void
): Promise<void> => {
    const teamsClient = new TeamsClient();
    const team = await teamsClient.getById(teamId);

    if (team.players && team.players.length > 0) {
        throw new Error('This team already has players. Remove them before filling placeholders.');
    }

    const personsClient = new PersonsClient();
    const activeFrom = new Date();
    const clubs = team.clubId
        ? [
              new CreateClubPlayerCommandOfClubPlayer({
                  playerId: 0,
                  clubId: team.clubId,
                  activeFrom,
                  activeTo: undefined,
              }),
          ]
        : undefined;

    for (let capNumber = 1; capNumber <= DUMMY_PLAYER_COUNT; capNumber += 1) {
        const positionId =
            capNumber === 1 ? PlayerPositionEnum.Goalkeeper : PlayerPositionEnum.Driver;

        await personsClient.create(
            new CreatePersonCommandOfPerson({
                name: DUMMY_PLAYER_NAME,
                surname: capNumber.toString(),
                birthDate: DUMMY_BIRTH_DATE,
                nationality: undefined,
                address: undefined,
                email: undefined,
                phone: undefined,
                facebook: undefined,
                youtube: undefined,
                twitter: undefined,
                instagram: undefined,
                photo: undefined,
                countryOfBirthId: undefined,
                cityOfBirthId: undefined,
                playerId: undefined,
                coachId: undefined,
                officialId: undefined,
                official: undefined,
                coach: undefined,
                player: new CreatePlayerCommand({
                    personId: 0,
                    primaryPositionId: positionId,
                    height: undefined,
                    weight: undefined,
                    dominantHand: undefined,
                    clubs,
                    allowWithoutSquad: false,
                    teams: [
                        new CreateTeamPlayerCommandOfTeamPlayer({
                            playerId: 0,
                            teamId,
                            positionId,
                            activeFrom,
                            activeTo: undefined,
                            capNumber,
                        }),
                    ],
                }),
            })
        );

        if (onProgress) {
            onProgress(capNumber, DUMMY_PLAYER_COUNT);
        }
    }
};
